const rootSelector = '[data-james-customer-reviews]';
const stateByRoot = new WeakMap();

/**
 * @param {HTMLElement} root
 */
function initialize(root) {
  if (stateByRoot.has(root)) return;

  const source = root.querySelector('.gw-rv-trust-badge-widget-placeholder');
  const output = root.querySelector('[data-james-customer-reviews-output]');
  const ratingOutput = root.querySelector('[data-james-customer-reviews-rating]');
  const countOutput = root.querySelector('[data-james-customer-reviews-count]');

  if (
    !(source instanceof HTMLElement) ||
    !(output instanceof HTMLElement) ||
    !(ratingOutput instanceof HTMLElement) ||
    !(countOutput instanceof HTMLElement)
  ) {
    return;
  }

  const state = {
    shadowObserver: /** @type {MutationObserver | undefined} */ (undefined),
    shadowRoot: /** @type {ShadowRoot | undefined} */ (undefined),
    discoveryIntervalId: /** @type {number | undefined} */ (undefined),
    timeoutId: /** @type {number | undefined} */ (undefined),
    cleanup: /** @type {(() => void) | undefined} */ (undefined),
  };

  stateByRoot.set(root, state);

  const stop = () => {
    state.shadowObserver?.disconnect();

    if (state.discoveryIntervalId !== undefined) {
      window.clearInterval(state.discoveryIntervalId);
    }

    if (state.timeoutId !== undefined) {
      window.clearTimeout(state.timeoutId);
    }

    stateByRoot.delete(root);
  };

  state.cleanup = stop;

  /**
   * @param {{ rating: string, count: number }} reviewData
   */
  const showReviewData = (reviewData) => {
    ratingOutput.textContent = reviewData.rating;
    countOutput.textContent = new Intl.NumberFormat().format(reviewData.count);
    updateStarFills(root, reviewData.rating);
    output.hidden = false;
    stop();
  };

  const attemptRead = () => {
    if (!state.shadowRoot) return;

    const reviewData = getReviewData(state.shadowRoot);
    if (reviewData) {
      showReviewData(reviewData);
    }
  };

  const discoverShadowRoot = () => {
    const shadowRoot = source.shadowRoot;
    if (!shadowRoot || state.shadowRoot === shadowRoot) return;

    state.shadowRoot = shadowRoot;

    if (state.discoveryIntervalId !== undefined) {
      window.clearInterval(state.discoveryIntervalId);
      state.discoveryIntervalId = undefined;
    }

    state.shadowObserver = new MutationObserver(attemptRead);
    state.shadowObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label'],
    });

    attemptRead();
  };

  state.timeoutId = window.setTimeout(stop, 12000);
  discoverShadowRoot();

  if (!state.shadowRoot) {
    state.discoveryIntervalId = window.setInterval(discoverShadowRoot, 200);
  }
}

/**
 * @param {ShadowRoot} shadowRoot
 * @returns {{ rating: string, count: number } | undefined}
 */
function getReviewData(shadowRoot) {
  const ratingElement = shadowRoot.querySelector('[role="img"][aria-label*="out of 5"]');
  const trustBadge = shadowRoot.querySelector('.gw-rv-trust-badge');
  const rating = getRating(ratingElement?.getAttribute('aria-label'));
  const count = getReviewCount(trustBadge);

  if (!rating || !count) return undefined;

  return { rating, count };
}

/**
 * @param {string | null | undefined} ariaLabel
 * @returns {string | undefined}
 */
function getRating(ariaLabel) {
  const match = ariaLabel?.match(/(\d+(?:[.,]\d+)?)\s*out of 5/i);
  const ratingText = match?.[1];
  if (!ratingText) return undefined;

  const rating = Number(ratingText.replace(',', '.'));
  if (!Number.isFinite(rating) || rating <= 0 || rating > 5) return undefined;

  return String(rating);
}

/**
 * @param {HTMLElement} root
 * @param {string} ratingValue
 */
function updateStarFills(root, ratingValue) {
  const rating = Math.min(Math.max(Number(ratingValue), 0), 5);

  root.querySelectorAll('[data-james-customer-reviews-star]').forEach((star, index) => {
    if (!(star instanceof HTMLElement)) return;

    const fillPercentage = Math.min(Math.max(rating - index, 0), 1) * 100;
    star.style.setProperty('--james-customer-reviews-star-fill', `${fillPercentage}%`);
  });
}

/**
 * @param {Element | null} trustBadge
 * @returns {number | undefined}
 */
function getReviewCount(trustBadge) {
  if (!trustBadge) return undefined;

  const verifiedText = trustBadge.querySelector('.gw-rv-trust-badge__verified-text');
  const countElement = verifiedText?.previousElementSibling;

  if (!(countElement instanceof HTMLParagraphElement)) return undefined;

  const count = Number(countElement.textContent?.replace(/\D/g, ''));
  if (!Number.isInteger(count) || count <= 0) return undefined;

  return count;
}

/**
 * @param {ParentNode} scope
 */
function initializeWithin(scope) {
  const roots = [
    ...(scope instanceof HTMLElement && scope.matches(rootSelector) ? [scope] : []),
    ...scope.querySelectorAll(rootSelector),
  ];

  roots.forEach((root) => {
    if (root instanceof HTMLElement) initialize(root);
  });
}

initializeWithin(document);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initializeWithin(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  const roots = [
    ...(event.target.matches(rootSelector) ? [event.target] : []),
    ...event.target.querySelectorAll(rootSelector),
  ];

  roots.forEach((root) => {
    if (!(root instanceof HTMLElement)) return;

    const state = stateByRoot.get(root);
    state?.cleanup?.();
  });
});